import React, { memo, useState } from "react";
import { OnlineUserEntry } from "store/onlineUsers";
import { useOnlineUsers } from "hooks/useOnlineUsers";
import { useOnlineUsersConnection } from "hooks/useOnlineUsers";
import CustomModal from "ui/modal/Modal";
import CustomButton from "ui/Button";
import { COLOR } from "utils/constant";

const formatLastActive = (isoString: string): string => {
    const date = new Date(isoString);
    const now = new Date();
    const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
    const diffMin = Math.floor(diffSec / 60);
    if (diffSec < 10) return "just now";
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffMin < 60) return `${diffMin}m ago`;
    return date.toLocaleTimeString();
};

const OnlineUserRow = memo(({ entry, selected, onToggle }: {
    entry: OnlineUserEntry;
    selected: boolean;
    onToggle: (username: string) => void;
}) => (
    <tr style={{ background: selected ? "#eef4ff" : undefined }}>
        <td style={{ padding: "0px 8px", borderBottom: "1px solid #eee" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0 }}>
                <input type="checkbox" checked={selected} onChange={() => onToggle(entry.username)} />
                <span className="online-dot" /> {entry.username}
            </label>
        </td>
        <td style={{ padding: "0px 8px", borderBottom: "1px solid #eee", color: "#555" }}>{entry.currentPage}</td>
        <td style={{ padding: "0px 8px", borderBottom: "1px solid #eee", color: "#999" }}>{formatLastActive(entry.lastUpdated)}</td>
    </tr>
));

const OnlineUsers: React.FC = () => {
    const { onlineUsers, count } = useOnlineUsers();
    const { sendMessage } = useOnlineUsersConnection();
    const [selected, setSelected] = useState<string[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [message, setMessage] = useState("");
    const [sent, setSent] = useState(false);

    const toggleUser = (username: string) => {
        setSelected((prev) =>
            prev.includes(username) ? prev.filter((u) => u !== username) : [...prev, username]
        );
    };

    const toggleAll = () => {
        setSelected(selected.length === onlineUsers.length ? [] : onlineUsers.map((u) => u.username));
    };

    const handleSend = () => {
        if (!message.trim()) return;
        sendMessage(message.trim(), selected);
        setMessage("");
        setSent(true);
        setTimeout(() => {
            setSent(false);
            setShowModal(false);
        }, 2000);
    };

    const handleClose = () => {
        setShowModal(false);
        setMessage("");
        setSent(false);
    };

    const modalTitle = selected.length === 0
        ? "Send message to all users"
        : `Send message to ${selected.length} selected user(s)`;

    return (
        <div className="online-users-panel">
            <h4>Online Users ({count})</h4>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd", width: "40%" }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0 }}>
                                <input
                                    type="checkbox"
                                    checked={selected.length === onlineUsers.length && onlineUsers.length > 0}
                                    onChange={toggleAll}
                                />
                                User
                            </label>
                        </th>
                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd", width: "40%" }}>Current Page</th>
                        <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #ddd", width: "20%" }}>Last Active</th>
                    </tr>
                </thead>
                <tbody>
                    {onlineUsers.map((entry) => (
                        <OnlineUserRow
                            key={entry.username}
                            entry={entry}
                            selected={selected.includes(entry.username)}
                            onToggle={toggleUser}
                        />
                    ))}
                </tbody>
            </table>

            <div style={{ marginTop: 16 }}>
                <CustomButton
                    label={selected.length === 0 ? "Send message to all" : `Send message to ${selected.length} selected`}
                    onClick={() => setShowModal(true)}
                    bgColor={COLOR.Green}
                />
            </div>

            {showModal && (
                <CustomModal
                    title={modalTitle}
                    onClose={handleClose}
                    actions={[
                        {
                            label: "Cancel",
                            onClick: handleClose,
                        },
                        {
                            label: sent ? "✓ Sent!" : "Send",
                            onClick: handleSend,
                            disabled: !message.trim() || sent,
                            bgColor: COLOR.Green,
                        },
                    ]}
                >
                    {sent ? (
                        <p style={{ color: "green", margin: 0 }}>✓ Message sent successfully.</p>
                    ) : (
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={5}
                            autoFocus
                            style={{ width: "100%", padding: 8, borderRadius: 4, border: "1px solid #ccc", resize: "vertical", boxSizing: "border-box", fontSize: 14 }}
                            placeholder="Type your message..."
                        />
                    )}
                </CustomModal>
            )}
        </div>
    );
};

export default OnlineUsers;